/**
 * @fileoverview Difficulty Card Component
 * @description Card for selecting difficulty level in the lobby
 */

import React, { memo } from "react";
import { View, Text, Pressable, useColorScheme } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { SudokuDifficulty, DifficultyConfig } from "../types";

interface DifficultyCardProps {
  /** Difficulty configuration */
  config: DifficultyConfig;
  /** Number of available puzzles */
  puzzleCount: number;
  /** Best time for this difficulty (in seconds) */
  bestTime?: number | null;
  /** Games played at this difficulty */
  gamesPlayed?: number;
  /** Games won at this difficulty */
  gamesWon?: number;
  /** Points awarded (weekend mode) */
  points?: number;
  /** Whether this is for weekend mode */
  isWeekendMode?: boolean;
  /** Called when card is pressed */
  onPress: (difficulty: SudokuDifficulty) => void;
  /** Whether the card is disabled */
  disabled?: boolean;
}

/**
 * Card for selecting a difficulty level
 */
export const DifficultyCard = memo<DifficultyCardProps>(
  ({
    config,
    puzzleCount,
    bestTime,
    gamesPlayed = 0,
    gamesWon = 0,
    points,
    isWeekendMode = false,
    onPress,
    disabled = false,
  }) => {
    const colorScheme = useColorScheme();
    const isDark = colorScheme === "dark";

    const formatTime = (seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    const winRate =
      gamesPlayed > 0 ? Math.round((gamesWon / gamesPlayed) * 100) : 0;

    return (
      <Pressable
        onPress={() => onPress(config.difficulty)}
        disabled={disabled || puzzleCount === 0}
        accessibilityRole="button"
        accessibilityLabel={`${config.label} moeilijkheid`}
        accessibilityState={{ disabled: disabled || puzzleCount === 0 }}
        style={({ pressed }) => ({
          width: "100%",
          borderRadius: 16,
          padding: 16,
          backgroundColor: pressed
            ? isDark
              ? "#374151"
              : "#F3F4F6"
            : isDark
            ? "#1F2937"
            : "#FFFFFF",
          borderWidth: 2,
          borderColor: config.color + "40",
          opacity: disabled || puzzleCount === 0 ? 0.5 : 1,
          marginBottom: 12,
        })}
      >
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            {/* Color indicator */}
            <View
              style={{
                width: 12,
                height: 12,
                borderRadius: 6,
                backgroundColor: config.color,
              }}
            />
            <Text
              style={{
                fontSize: 20,
                fontWeight: "700",
                color: isDark ? "#F9FAFB" : "#111827",
              }}
            >
              {config.label}
            </Text>
          </View>

          {/* Points badge (weekend mode) */}
          {isWeekendMode && points !== undefined && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 4,
                backgroundColor: config.color + "20",
                paddingHorizontal: 10,
                paddingVertical: 4,
                borderRadius: 12,
              }}
            >
              <Ionicons name="star" size={14} color={config.color} />
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: "700",
                  color: config.color,
                }}
              >
                {points} pt
              </Text>
            </View>
          )}
        </View>

        {/* Description */}
        <Text
          style={{
            fontSize: 14,
            color: isDark ? "#9CA3AF" : "#6B7280",
            marginBottom: 12,
          }}
        >
          {config.description}
        </Text>

        {/* Stats row */}
        <View
          style={{
            flexDirection: "row",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          {/* Puzzles available */}
          <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
            <Ionicons
              name="grid-outline"
              size={16}
              color={isDark ? "#9CA3AF" : "#6B7280"}
            />
            <Text
              style={{
                fontSize: 13,
                color: isDark ? "#9CA3AF" : "#6B7280",
              }}
            >
              {puzzleCount} puzzels
            </Text>
          </View>

          {/* Best time */}
          {bestTime !== null && bestTime !== undefined && (
            <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
              <Ionicons
                name="trophy-outline"
                size={16}
                color="#F59E0B"
              />
              <Text
                style={{
                  fontSize: 13,
                  color: "#F59E0B",
                  fontWeight: "600",
                }}
              >
                {formatTime(bestTime)}
              </Text>
            </View>
          )}

          {/* Games played */}
          {gamesPlayed > 0 && (
            <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
              <Ionicons
                name="game-controller-outline"
                size={16}
                color={isDark ? "#9CA3AF" : "#6B7280"}
              />
              <Text
                style={{
                  fontSize: 13,
                  color: isDark ? "#9CA3AF" : "#6B7280",
                }}
              >
                {gamesWon}/{gamesPlayed} ({winRate}%)
              </Text>
            </View>
          )}
        </View>

        {/* Game limits info */}
        <View
          style={{
            flexDirection: "row",
            gap: 12,
            marginTop: 12,
            paddingTop: 12,
            borderTopWidth: 1,
            borderTopColor: isDark ? "#374151" : "#E5E7EB",
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
            <Ionicons
              name="close-circle-outline"
              size={14}
              color={isDark ? "#6B7280" : "#9CA3AF"}
            />
            <Text
              style={{
                fontSize: 12,
                color: isDark ? "#6B7280" : "#9CA3AF",
              }}
            >
              Max {config.max_mistakes} fouten
            </Text>
          </View>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
            <Ionicons
              name="bulb-outline"
              size={14}
              color={isDark ? "#6B7280" : "#9CA3AF"}
            />
            <Text
              style={{
                fontSize: 12,
                color: isDark ? "#6B7280" : "#9CA3AF",
              }}
            >
              {config.max_hints} hints
            </Text>
          </View>
        </View>

        {/* Start Game Button */}
        <View
          style={{
            marginTop: 16,
            backgroundColor: config.color,
            borderRadius: 12,
            paddingVertical: 12,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            opacity: puzzleCount === 0 ? 0.5 : 1,
          }}
        >
          <Ionicons name="play" size={18} color="#FFFFFF" />
          <Text
            style={{
              fontSize: 16,
              fontWeight: "700",
              color: "#FFFFFF",
            }}
          >
            Start Spel
          </Text>
        </View>
      </Pressable>
    );
  }
);

DifficultyCard.displayName = "DifficultyCard";

